import React, { useState, useCallback, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { ICrmActivityCategory, ICrmActivityCategoryGroup } from 'services/CrmApi/types/CrmActivityTypes';
import CloseXComponent from 'ui/Icons/components/CloseX.component';
import { TimesIcon } from 'ui/Icons/components/Times.component';
import { SvgIcon } from 'ui/SvgIcon';
import CircleXIcon from 'ui/Icons/tabler/circle-x.component.svg';
export interface ICategoryTagProps {
  id: string;
  name: string;
  onRemove?: (id: string) => void;
}

export const CategoryTag: React.FC<ICategoryTagProps> = ({ id, name, onRemove }) => {
  return (
    <div key={id} className="flex items-center gap-[10px] bg-gray-10 p-[5px] rounded">
      <span className="font-pt-sans text-[15px] leading-[19px] text-black">{name}</span>
      {onRemove && (
        <span className="relative top-[-1px] cursor-pointer" onClick={() => onRemove(id)}>
          <TimesIcon />
        </span>
      )}
    </div>
  );
};

export interface ICategoryDropdownProps {
  optionGroups: ICrmActivityCategoryGroup[];
  selectedOptions: string[];
  onSelectedOptionsChange: (categoryIds: string[]) => void;
  hasError?: boolean;
  innerTextInputClassName?: string;
  noOptionsMessage: string;
  allowMultipleSelectedOptions?: boolean;
}

export const CategoryDropdown: React.FC<ICategoryDropdownProps> = ({
  optionGroups,
  selectedOptions,
  onSelectedOptionsChange,
  hasError = false,
  innerTextInputClassName,
  noOptionsMessage,
  allowMultipleSelectedOptions = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const shouldRenderInput = allowMultipleSelectedOptions || selectedOptions.length === 0;
  const shouldRenderClearAll = selectedOptions.length > 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFilterText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get all options from all groups
  const allOptions = optionGroups.flatMap(group =>
    group.categories.map(category => ({
      ...category,
      groupName: group.name,
    }))
  );
  const allOptionsDict = allOptions.reduce((acc, option) => {
    acc[option.id] = option;
    return acc;
  }, {} as Record<string, ICrmActivityCategory>);

  // Filter categories based on search text and selected state
  const filteredOptions = allOptions.filter(option => {
    const matchesFilter = option.name.toLowerCase().includes(filterText.toLowerCase());
    const isNotSelected = !selectedOptions.includes(option.id);
    return matchesFilter && isNotSelected;
  });

  const filteredCategoryGroups = optionGroups
    .map(group => ({
      ...group,
      categories: group.categories.filter(option => filteredOptions.some(fc => fc.id === option.id)),
    }))
    .filter(group => group.categories.length > 0);

  const selectedOptionObjects = selectedOptions.map(optionId => allOptionsDict[optionId]).filter(Boolean);

  const handleInputFocus = useCallback(() => {
    if (!allowMultipleSelectedOptions && selectedOptions.length > 0) {
      return;
    }
    setIsOpen(true);
  }, [allowMultipleSelectedOptions, selectedOptions.length]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setFilterText(value);
  }, []);

  const handleOptionSelect = useCallback(
    (optionId: string) => {
      const newSelectedOptions = [...selectedOptions, optionId];
      onSelectedOptionsChange(newSelectedOptions);
      setInputValue('');
      setFilterText('');

      // When allowMultipleSelectedOptions is false, close dropdown immediately after selection
      if (!allowMultipleSelectedOptions) {
        setIsOpen(false);
      } else {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (!isTouchDevice) {
          inputRef.current?.focus();
        }
      }
    },
    [selectedOptions, onSelectedOptionsChange, allowMultipleSelectedOptions]
  );

  const handleOptionRemove = useCallback(
    (optionId: string) => {
      const newSelectedOptions = selectedOptions.filter(id => id !== optionId);
      onSelectedOptionsChange(newSelectedOptions);
    },
    [selectedOptions, onSelectedOptionsChange]
  );

  const handleClearAll = useCallback(
    e => {
      e.stopPropagation();
      onSelectedOptionsChange([]);
      setInputValue('');
      setFilterText('');
    },
    [onSelectedOptionsChange]
  );

  return (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={classnames('relative flex w-full min-h-[48px] border border-solid', 'items-center p-2', {
          'cursor-text': allowMultipleSelectedOptions || selectedOptions.length === 0,
          'cursor-default': !allowMultipleSelectedOptions && selectedOptions.length > 0,
          'border-green-prime': isOpen,
          'border-gray-40': !isOpen,
          'bg-white-hard': !hasError,
          'border-red-95 bg-red-25': hasError,
        })}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {/* Selected Categories Tags */}
          {selectedOptionObjects.map(option => (
            <CategoryTag key={option.id} id={option.id} name={option.name} onRemove={handleOptionRemove} />
          ))}

          {shouldRenderInput && (
            <input
              ref={inputRef}
              type="text"
              name="crmcategory"
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder={selectedOptions.length === 0 ? 'Select ...' : ''}
              className={classnames(
                'flex-1 min-w-[100px] outline-none bg-transparent text-sm',
                innerTextInputClassName
              )}
              data-lpignore="true"
              autoComplete="new-password"
            />
          )}
        </div>

        {shouldRenderClearAll && (
          <SvgIcon
            IconComponent={CircleXIcon}
            onClick={handleClearAll}
            className="w-5 h-5 fill-brown-100 hover:fill-brown-120 cursor-pointer relative"
          />
        )}
      </div>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white-hard border border-solid border-gray-40 rounded shadow-lg max-h-[300px] overflow-y-auto">
          {filteredCategoryGroups.length === 0 ? (
            <div className="p-3 text-gray-100 text-sm">{noOptionsMessage}</div>
          ) : (
            <div className="p-2">
              {filteredCategoryGroups.map(group => (
                <div key={group.id} className="mb-4 last:mb-0">
                  {/* Group Header */}
                  <div className="font-pt-sans text-[15px] font-bold text-black mb-[10px]">{group.name}</div>

                  <div className="flex flex-wrap gap-2">
                    {group.categories.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleOptionSelect(option.id)}
                        className="bg-white font-pt-sans text-[13px] text-black p-[5px] rounded border border-solid border-gray-20 cursor-pointer transition-colors"
                      >
                        {option.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
